import { For, Show, createEffect, createSignal } from "solid-js";
import { appendUniqueColors, generateColorScheme, hslToHex } from "../color";
import { styles } from "../styles";
import { COLOR_SEARCH_TARGETS, type ColorFilters, type ColorSchemeType, type HslColorFilter, type PaletteRankLimit } from "../types";
import { HslColorPicker } from "./HslColorPicker";

type Props = {
  open: boolean;
  filteredCount: number;
  totalCount: number;
  filters: ColorFilters;
  onPatch: (patch: Partial<ColorFilters>) => void;
  onReset: () => void;
  onToggleOpen: () => void;
};

export function ColorPanel(props: Props) {
  const [matchHelpOpen, setMatchHelpOpen] = createSignal(false);
  const [paletteRankHelpOpen, setPaletteRankHelpOpen] = createSignal(false);
  const [pendingColorRemoval, setPendingColorRemoval] = createSignal<string | null>(null);
  const [colorSchemeType, setColorSchemeType] = createSignal<ColorSchemeType>("complementary");
  const [selectedColor, setSelectedColor] = createSignal<HslColorFilter>({
    hue: 330,
    saturation: 75,
    lightness: 60,
  });

  createEffect(() => {
    const pending = pendingColorRemoval();
    if (!props.open || (pending && !props.filters.hslColors.some((color) => hslToHex(color) === pending))) {
      setPendingColorRemoval(null);
    }
  });

  const addSelectedColor = () => {
    props.onPatch({ hslColors: appendUniqueColors(props.filters.hslColors, [selectedColor()]) });
  };
  const schemeColors = () => generateColorScheme(selectedColor(), colorSchemeType());
  const addColorScheme = () => {
    props.onPatch({ hslColors: appendUniqueColors(props.filters.hslColors, schemeColors()) });
  };
  const toggleTarget = (target: (typeof COLOR_SEARCH_TARGETS)[number]) => {
    props.onPatch({
      targets: props.filters.targets.includes(target)
        ? props.filters.targets.filter((current) => current !== target)
        : [...props.filters.targets, target],
    });
  };

  return (
    <div class={styles.popoverRoot}>
      <button class={styles.headerButton} type="button" title="色検索" aria-label="色検索" aria-expanded={props.open} aria-controls="color-search-panel" onClick={props.onToggleOpen}>🎨</button>
      <Show when={props.open}>
      <div id="color-search-panel" class={styles.popover}>
        <header class={styles.popoverHeader}>
          <div class={styles.popoverTitleRow}>
            <strong class={styles.popoverTitle}>色検索</strong>
            <span class={styles.popoverCount}>{props.filteredCount.toLocaleString()}/{props.totalCount.toLocaleString()}</span>
          </div>
          <button class={styles.popoverCloseButton} type="button" aria-label="色検索を閉じる" onClick={props.onToggleOpen}>✕</button>
        </header>
        <div class={styles.colorGrid}>
          <div class={styles.field} style={{ "grid-column": "1 / -1" }}>
            <fieldset class={styles.colorTargetFieldset}>
              <legend class={styles.label}>検索色</legend>
              <div class={styles.searchColorPalette} aria-label="検索する色">
              <For each={props.filters.hslColors}>{(color, index) => (
                <button
                  class={styles.searchColorChip}
                  type="button"
                  title={pendingColorRemoval() === hslToHex(color)
                    ? `もう一度押して${hslToHex(color)}を削除`
                    : `${hslToHex(color)}の削除を選択`}
                  aria-label={pendingColorRemoval() === hslToHex(color)
                    ? `もう一度押して${hslToHex(color)}を検索色から削除`
                    : `${hslToHex(color)}の削除を選択`}
                  aria-pressed={pendingColorRemoval() === hslToHex(color)}
                  style={{ "background-color": hslToHex(color) }}
                  onClick={() => {
                    const hex = hslToHex(color);
                    if (pendingColorRemoval() !== hex) {
                      setSelectedColor({ ...color });
                      setPendingColorRemoval(hex);
                      return;
                    }
                    setPendingColorRemoval(null);
                    props.onPatch({
                      hslColors: props.filters.hslColors.filter((_, currentIndex) => currentIndex !== index()),
                    });
                  }}
                ><Show when={pendingColorRemoval() === hslToHex(color)}><span>×</span></Show></button>
              )}</For>
              <Show when={props.filters.hslColors.length === 0}>
                <span class={styles.facetCount}>色はまだ追加されていません。</span>
              </Show>
              </div>
            </fieldset>
            <fieldset class={styles.colorTargetFieldset}>
              <legend class={styles.label}>検索対象</legend>
              <div class={styles.colorTargetChecklist}>
                <div class={styles.colorPaletteRankLimit}>
                  <select
                    class={styles.colorPaletteRankSelect}
                    value={props.filters.paletteRankLimit}
                    aria-label="検索する代表色の順位"
                    onChange={(event) => props.onPatch({ paletteRankLimit: Number(event.currentTarget.value) as PaletteRankLimit })}
                  >
                    <For each={[1, 2, 3, 4, 5] as const}>{(rank) => <option value={rank}>{rank}</option>}</For>
                  </select>
                  <span>位まで</span>
                  <button
                    class={styles.helpButton}
                    type="button"
                    aria-label="検索する代表色の順位について"
                    aria-expanded={paletteRankHelpOpen()}
                    aria-controls="palette-rank-help"
                    onClick={() => setPaletteRankHelpOpen((open) => !open)}
                  >?</button>
                </div>
                <For each={COLOR_SEARCH_TARGETS}>{(target) => (
                  <label class={styles.colorTargetCheck}>
                    <input
                      type="checkbox"
                      checked={props.filters.targets.includes(target)}
                      onChange={() => toggleTarget(target)}
                    />
                    <span>{target}</span>
                  </label>
                )}</For>
              </div>
              <Show when={paletteRankHelpOpen()}>
                <div id="palette-rank-help" class={styles.colorMatchHelp}>
                  コーデ画像中で最も多い面積で使われている色を一位として、五位まで含めて検索できます。
                </div>
              </Show>
              <Show when={props.filters.targets.length === 0}>
                <span class={styles.colorTargetWarning}>検索対象を1つ以上選択してください。</span>
              </Show>
            </fieldset>
            <fieldset class={styles.colorMatchControl}>
              <legend class={styles.colorMatchLegend}>
                <span class={styles.label}>色の許容範囲</span>
                <button
                  class={styles.helpButton}
                  type="button"
                  aria-label="色の許容範囲について"
                  aria-expanded={matchHelpOpen()}
                  aria-controls="color-match-help"
                  onClick={() => setMatchHelpOpen((open) => !open)}
                >?</button>
              </legend>
              <div class={styles.colorMatchInputRow}>
                <input
                  id="color-match-tolerance"
                  class={styles.colorMatchRange}
                  type="range"
                  min="0"
                  max="20"
                  step="1"
                  value={props.filters.colorMatchTolerance}
                  onInput={(event) => props.onPatch({ colorMatchTolerance: event.currentTarget.valueAsNumber })}
                />
                <output class={styles.colorMatchValue} for="color-match-tolerance">
                  {props.filters.colorMatchTolerance}
                </output>
              </div>
              <Show when={matchHelpOpen()}>
                <div id="color-match-help" class={styles.colorMatchHelp}>
                  画像から抽出した色との見た目の差を、Lab色空間のΔEで比較します。
                  0は完全一致で、値を大きくするほど幅広い色が一致します。
                  1〜2はほぼ同じ色、3〜5はかなり近い色、6〜10は似た色、11〜20は広めの検索が目安です。
                </div>
              </Show>
            </fieldset>
            <div class={styles.colorSelectionArea}>
              <HslColorPicker
                value={selectedColor()}
                onChange={setSelectedColor}
              />
              <div class={styles.selectedColorActions}>
                <span
                  class={styles.selectedColorPreview}
                  style={{ "background-color": hslToHex(selectedColor()) }}
                  aria-label={`選択色 ${hslToHex(selectedColor())}`}
                />
                <button class={styles.button} type="button" onClick={addSelectedColor}>この色を追加</button>
                <div class={styles.colorSchemeControls}>
                  <label class={styles.label} for="color-scheme-type">配色タイプ</label>
                  <select
                    id="color-scheme-type"
                    class={styles.input}
                    value={colorSchemeType()}
                    onInput={(event) => setColorSchemeType(event.currentTarget.value as ColorSchemeType)}
                  >
                    <option value="analogous">類似色</option>
                    <option value="complementary">補色</option>
                    <option value="split-complement">分割補色</option>
                    <option value="triadic">トライアド</option>
                    <option value="tetradic">テトラード</option>
                  </select>
                  <div class={styles.colorSchemePreview} aria-label="追加される配色">
                    <For each={schemeColors()}>{(color) => (
                      <span
                        class={styles.colorSchemePreviewChip}
                        style={{ "background-color": hslToHex(color) }}
                        title={hslToHex(color)}
                      />
                    )}</For>
                  </div>
                  <button class={styles.button} type="button" onClick={addColorScheme}>
                    配色セットを追加
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class={styles.inline} style={{ "margin-top": "1rem" }}>
          <button class={styles.button} type="button" onClick={() => {
            setPendingColorRemoval(null);
            props.onReset();
          }}>色検索をリセット</button>
        </div>
      </div>
      </Show>
    </div>
  );
}
